import * as React from 'react';
import { CheckMark, Close, Deduct } from '@components/atoms/icons';
import { LoadingSpinner } from '@components/atoms/loading-spinner/loading-spinner';
import { LoadingSpinnerSize } from '@components/atoms/loading-spinner/loading-spinner.types';
import { cn } from '@utils';
import { PositionToggleButton } from './position-toggle-button';
import { TONE_DOT_CLASS } from './tone';
import { FloatingPanelToneEnum } from './types';
import type { IFloatingNotificationPanelLabels, IPositionMoveControl } from './types';

const VIEWPORT_GUTTER_MAX_HEIGHT_CLASS = 'max-h-[calc(100vh-2rem)]';

interface IExpandedPanelProps {
	labels: IFloatingNotificationPanelLabels;
	tone: FloatingPanelToneEnum;
	onMinimize?: () => void;
	onDismiss?: () => void;
	moveControl?: IPositionMoveControl;
	maxContentHeight: string;
	panelWidth: string;
	hasChildren: boolean;
	footer?: React.ReactNode;
	children?: React.ReactNode;
}

interface IToneIndicatorProps {
	tone: FloatingPanelToneEnum;
	label: string;
}

const ToneIndicator: React.FC<IToneIndicatorProps> = ({ tone, label }) => {
	if (tone === FloatingPanelToneEnum.LOADING) {
		return (
			<span
				className="size-3.5 flex items-center justify-center"
				data-testid="expanded-panel-tone"
				data-tone={tone}
				role="img"
				aria-label={label}
			>
				<LoadingSpinner size={LoadingSpinnerSize.SM} className="size-3" />
			</span>
		);
	}

	return (
		<div
			className={cn('size-2 rounded-full', TONE_DOT_CLASS[tone])}
			data-testid="expanded-panel-tone"
			data-tone={tone}
			role="img"
			aria-label={label}
		/>
	);
};

/**
 * Expanded list panel. Capped to the viewport so bottom placement cannot push
 * the header (move, minimize, dismiss) off-screen.
 */
export const ExpandedPanel: React.FC<IExpandedPanelProps> = ({
	labels,
	tone,
	onMinimize,
	onDismiss,
	moveControl,
	maxContentHeight,
	panelWidth,
	hasChildren,
	footer,
	children,
}) => {
	return (
		<div
			className={cn(
				'bg-white',
				'border border-borderPrimary',
				'rounded-2xl',
				'shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25),0_0_0_1px_rgba(0,0,0,0.05)]',
				'overflow-hidden',
				'flex flex-col',
				VIEWPORT_GUTTER_MAX_HEIGHT_CLASS
			)}
			style={{ width: panelWidth }}
			data-testid="expanded-panel"
		>
			<div className="flex items-center justify-between px-4 py-3 border-b border-borderSeparator bg-bgPrimary shrink-0">
				<div className="flex items-center gap-2">
					<ToneIndicator tone={tone} label={labels.toneAriaLabel(tone)} />
					<span className="text-sm font-semibold text-primary tracking-tight">{labels.title}</span>
				</div>
				<div className="flex items-center gap-1">
					{moveControl && (
						<PositionToggleButton moveControl={moveControl} testId="expanded-panel-move" />
					)}
					{onMinimize && (
						<button
							type="button"
							onClick={onMinimize}
							className="size-7 flex items-center justify-center text-secondary hover:text-primary hover:bg-black/5 rounded-lg transition-all duration-150"
							aria-label={labels.minimizeAriaLabel}
							title={labels.minimizeAriaLabel}
						>
							<Deduct className="size-4" />
						</button>
					)}
					{onDismiss && (
						<button
							type="button"
							onClick={onDismiss}
							className="size-7 flex items-center justify-center text-secondary hover:text-primary hover:bg-black/5 rounded-lg transition-all duration-150"
							aria-label={labels.dismissAriaLabel}
							title={labels.dismissAriaLabel}
						>
							<Close className="size-4" />
						</button>
					)}
				</div>
			</div>

			<div
				className="overflow-y-auto min-h-0 flex-1"
				style={{ maxHeight: maxContentHeight }}
				data-testid="expanded-panel-body"
			>
				{hasChildren ? (
					<div className="p-2 space-y-1">{children}</div>
				) : (
					<div className="py-8 px-4 text-center">
						<div className="size-12 mx-auto mb-3 rounded-full bg-bgPrimary flex items-center justify-center">
							<CheckMark className="size-6 text-tertiary" />
						</div>
						<div className="text-sm text-secondary" data-testid="expanded-panel-empty-state">
							{labels.emptyState}
						</div>
					</div>
				)}
				{footer && (
					<div className="flex items-center justify-center px-2 pb-2 pt-1" data-testid="expanded-panel-footer">
						{footer}
					</div>
				)}
			</div>
		</div>
	);
};
